import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authApi } from "../lib/api";

const AuthContext = createContext(null);

export const AUTH_STATUSES = Object.freeze({
  LOADING: "loading",
  AUTHENTICATED: "authenticated",
  UNAUTHENTICATED: "unauthenticated",
  UNAVAILABLE: "unavailable",
});

const DEFAULT_LOCAL_AUTH_PROFILE = Object.freeze({
  email: "local-demo@venturecopilot.dev",
  name: "Local Demo User",
});

const TRUE_VALUES = new Set(["1", "true", "yes", "on"]);

export const parseBooleanEnv = (value) => {
  if (typeof value === "string") {
    return TRUE_VALUES.has(value.trim().toLowerCase());
  }

  return Boolean(value);
};

export const getAuthConfiguration = (env = {}) => ({
  backendUrl: env.REACT_APP_BACKEND_URL || "",
  localAuthEnabled: parseBooleanEnv(env.REACT_APP_ENABLE_LOCAL_AUTH_BYPASS),
  localAuthProfile: {
    email: env.REACT_APP_LOCAL_AUTH_EMAIL || DEFAULT_LOCAL_AUTH_PROFILE.email,
    name: env.REACT_APP_LOCAL_AUTH_NAME || DEFAULT_LOCAL_AUTH_PROFILE.name,
    picture: env.REACT_APP_LOCAL_AUTH_PICTURE || null,
  },
});

export const normalizeAuthUser = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const userId = payload.user_id || payload.id || payload.email;
  if (!userId) {
    return null;
  }

  return {
    ...payload,
    id: userId,
    user_id: payload.user_id || userId,
    name: payload.name || payload.email || "Authorized User",
    email: payload.email || null,
    picture: payload.picture || null,
  };
};

const getErrorStatus = (error) => error?.response?.status ?? null;

export const resolveSessionRefreshError = (error) => {
  const status = getErrorStatus(error);

  if (status === 401) {
    return {
      status: AUTH_STATUSES.UNAUTHENTICATED,
      message: null,
    };
  }

  if (!status) {
    return {
      status: AUTH_STATUSES.UNAVAILABLE,
      message: "We couldn't reach the authentication service. Make sure the backend is running and try again.",
    };
  }

  return {
    status: AUTH_STATUSES.UNAVAILABLE,
    message: "Authentication is temporarily unavailable. Please try again in a moment.",
  };
};

export const resolveBootstrapAuthFailure = (error, runtimeConfig = null) => {
  const baseFailure = resolveSessionRefreshError(error);
  if (baseFailure.status !== AUTH_STATUSES.UNAVAILABLE) {
    return baseFailure;
  }

  if (!runtimeConfig || typeof runtimeConfig !== "object") {
    return baseFailure;
  }

  // Passcode login now issues JWTs that work without a database connection.
  // Show the passcode form whenever ACCESS_PASSCODE is configured, regardless of DB state.
  if (runtimeConfig.passcode_login_enabled) {
    return {
      status: AUTH_STATUSES.UNAUTHENTICATED,
      message: runtimeConfig.session_backend_ready
        ? "We couldn't restore your existing session, but entry-code sign-in is still available below."
        : null,
    };
  }

  if (runtimeConfig.session_backend_ready) {
    return {
      status: AUTH_STATUSES.UNAUTHENTICATED,
      message: null,
    };
  }

  if (runtimeConfig.public_message) {
    return {
      status: AUTH_STATUSES.UNAVAILABLE,
      message: runtimeConfig.public_message,
    };
  }

  return baseFailure;
};

export const resolveSessionExchangeError = (error) => {
  const status = getErrorStatus(error);

  if (status === 400 || status === 401) {
    return {
      status: AUTH_STATUSES.UNAUTHENTICATED,
      message: "That access link is invalid or has expired.",
    };
  }

  if (!status) {
    return {
      status: AUTH_STATUSES.UNAVAILABLE,
      message: "We couldn't reach the authentication service. Try again once the backend is available.",
    };
  }

  return {
    status: AUTH_STATUSES.UNAVAILABLE,
    message: "We couldn't complete sign-in right now. Please try again.",
  };
};

export const resolveLocalBypassError = (error) => {
  const status = getErrorStatus(error);

  if (status === 404) {
    return {
      status: AUTH_STATUSES.UNAUTHENTICATED,
      message: "Local access is disabled on the backend. Set LOCAL_AUTH_BYPASS=true for local development.",
    };
  }

  if (!status) {
    return {
      status: AUTH_STATUSES.UNAVAILABLE,
      message: "We couldn't reach the authentication service. Start the backend and try again.",
    };
  }

  return {
    status: AUTH_STATUSES.UNAVAILABLE,
    message: "We couldn't create a local session right now. Please try again.",
  };
};

export const resolvePasscodeLoginError = (error) => {
  const status = getErrorStatus(error);

  if (status === 401) {
    return {
      status: AUTH_STATUSES.UNAUTHENTICATED,
      message: "That entry code is invalid. Please try again.",
    };
  }

  if (status === 404) {
    return {
      status: AUTH_STATUSES.UNAUTHENTICATED,
      message: "Entry-code access isn't enabled for this environment yet.",
    };
  }

  if (!status) {
    return {
      status: AUTH_STATUSES.UNAVAILABLE,
      message: "We couldn't reach the authentication service. Make sure the backend is running and try again.",
    };
  }

  return {
    status: AUTH_STATUSES.UNAVAILABLE,
    message: "We couldn't complete entry-code sign-in right now. Please try again.",
  };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const authConfig = useMemo(() => getAuthConfiguration(process.env), []);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authStatus, setAuthStatus] = useState(AUTH_STATUSES.LOADING);
  const [backendAuthConfig, setBackendAuthConfig] = useState(null);

  const applyAuthSuccess = useCallback((payload) => {
    const normalizedUser = normalizeAuthUser(payload);

    setUser(normalizedUser);
    setError(null);
    setAuthStatus(normalizedUser ? AUTH_STATUSES.AUTHENTICATED : AUTH_STATUSES.UNAUTHENTICATED);

    return normalizedUser;
  }, []);

  const applyAuthFailure = useCallback((failure) => {
    setUser(null);
    setAuthStatus(failure.status);
    setError(failure.message);
    return null;
  }, []);

  const checkAuth = useCallback(async () => {
    setLoading(true);
    setError(null);
    let runtimeConfig = null;

    try {
      const configResponse = await authApi.getConfig();
      runtimeConfig = configResponse.data;
      setBackendAuthConfig(runtimeConfig);
    } catch (_) {
      setBackendAuthConfig(null);
    }

    try {
      const response = await authApi.getMe();
      return applyAuthSuccess(response.data);
    } catch (requestError) {
      return applyAuthFailure(resolveBootstrapAuthFailure(requestError, runtimeConfig));
    } finally {
      setLoading(false);
    }
  }, [applyAuthFailure, applyAuthSuccess]);

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  const loginWithLocalBypass = useCallback(
    async (overrides = {}) => {
      if (!authConfig.localAuthEnabled) {
        const failure = {
          status: AUTH_STATUSES.UNAUTHENTICATED,
          message: "Local access isn't enabled for this frontend environment.",
        };
        applyAuthFailure(failure);
        return { ok: false, message: failure.message };
      }

      setLoading(true);
      setError(null);

      try {
        const payload = {
          ...authConfig.localAuthProfile,
          ...overrides,
        };
        const response = await authApi.devLogin(payload);
        const normalizedUser = applyAuthSuccess(response.data);
        return { ok: true, user: normalizedUser };
      } catch (requestError) {
        const failure = resolveLocalBypassError(requestError);
        applyAuthFailure(failure);
        return { ok: false, message: failure.message };
      } finally {
        setLoading(false);
      }
    },
    [applyAuthFailure, applyAuthSuccess, authConfig.localAuthEnabled, authConfig.localAuthProfile],
  );

  const loginWithPasscode = useCallback(
    async (passcode) => {
      setLoading(true);
      setError(null);

      try {
        const response = await authApi.passcodeLogin({ passcode });
        const normalizedUser = applyAuthSuccess(response.data);
        return { ok: true, user: normalizedUser };
      } catch (requestError) {
        const failure = resolvePasscodeLoginError(requestError);
        applyAuthFailure(failure);
        return { ok: false, message: failure.message };
      } finally {
        setLoading(false);
      }
    },
    [applyAuthFailure, applyAuthSuccess],
  );

  const exchangeSession = useCallback(
    async (sessionId) => {
      setLoading(true);
      setError(null);

      try {
        const response = await authApi.exchangeSession(sessionId);
        return applyAuthSuccess(response.data);
      } catch (requestError) {
        applyAuthFailure(resolveSessionExchangeError(requestError));
        throw requestError;
      } finally {
        setLoading(false);
      }
    },
    [applyAuthFailure, applyAuthSuccess],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (_) {
      // Preserve logout UX even if the backend session has already disappeared.
    } finally {
      setUser(null);
      setError(null);
      setLoading(false);
      setAuthStatus(AUTH_STATUSES.UNAUTHENTICATED);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      authStatus,
      authMode: authConfig.localAuthEnabled ? "local-bypass" : "session",
      localAuthEnabled: authConfig.localAuthEnabled,
      localAuthProfile: authConfig.localAuthProfile,
      backendAuthConfig,
      loginWithLocalBypass,
      loginWithPasscode,
      logout,
      exchangeSession,
      checkAuth,
      clearError,
      isAuthenticated: authStatus === AUTH_STATUSES.AUTHENTICATED && Boolean(user),
      authUnavailable: authStatus === AUTH_STATUSES.UNAVAILABLE,
    }),
    [
      authConfig.localAuthEnabled,
      authConfig.localAuthProfile,
      authStatus,
      backendAuthConfig,
      checkAuth,
      clearError,
      error,
      exchangeSession,
      loading,
      loginWithLocalBypass,
      loginWithPasscode,
      logout,
      user,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

if (process.env.NODE_ENV === "test" && typeof describe === "function" && typeof test === "function") {
  describe("getAuthConfiguration", () => {
    test("enables local bypass only for explicit truthy env values", () => {
      expect(getAuthConfiguration({ REACT_APP_ENABLE_LOCAL_AUTH_BYPASS: "true" }).localAuthEnabled).toBe(true);
      expect(getAuthConfiguration({ REACT_APP_ENABLE_LOCAL_AUTH_BYPASS: "false" }).localAuthEnabled).toBe(false);
      expect(getAuthConfiguration({}).localAuthEnabled).toBe(false);
    });

    test("hydrates local auth defaults without exposing a frontend passcode", () => {
      const config = getAuthConfiguration({});

      expect(config.localAuthProfile.email).toBe("local-demo@venturecopilot.dev");
      expect(config.localAuthProfile.name).toBe("Local Demo User");
      expect(config).not.toHaveProperty("passcode");
    });
  });

  describe("normalizeAuthUser", () => {
    test("maps backend auth payloads into the frontend user shape", () => {
      expect(
        normalizeAuthUser({
          user_id: "user_123",
          email: "investor@example.com",
          name: "Investor Demo",
        }),
      ).toEqual({
        id: "user_123",
        user_id: "user_123",
        email: "investor@example.com",
        name: "Investor Demo",
        picture: null,
      });
    });
  });

  describe("auth error resolvers", () => {
    test("treats missing session cookies as unauthenticated rather than unavailable", () => {
      expect(resolveSessionRefreshError({ response: { status: 401 } })).toEqual({
        status: AUTH_STATUSES.UNAUTHENTICATED,
        message: null,
      });
    });

    test("treats offline auth requests as unavailable", () => {
      expect(resolveSessionRefreshError({})).toEqual({
        status: AUTH_STATUSES.UNAVAILABLE,
        message: "We couldn't reach the authentication service. Make sure the backend is running and try again.",
      });
    });
  });

  describe("resolveBootstrapAuthFailure", () => {
    test("keeps the passcode form available when the backend reports auth is otherwise ready", () => {
      expect(
        resolveBootstrapAuthFailure(
          { request: {} },
          { session_backend_ready: true, passcode_login_enabled: true },
        ),
      ).toEqual({
        status: AUTH_STATUSES.UNAUTHENTICATED,
        message: "We couldn't restore your existing session, but entry-code sign-in is still available below.",
      });
    });

    test("shows passcode form even when database is degraded if passcode_login_enabled", () => {
      expect(
        resolveBootstrapAuthFailure(
          { request: {} },
          {
            session_backend_ready: false,
            passcode_login_enabled: true,
            public_message: "The backend reached Render, but it could not connect to MongoDB.",
          },
        ),
      ).toEqual({
        status: AUTH_STATUSES.UNAUTHENTICATED,
        message: null,
      });
    });

    test("surfaces backend readiness issues from the auth config endpoint", () => {
      expect(
        resolveBootstrapAuthFailure(
          { request: {} },
          {
            session_backend_ready: false,
            public_message: "The backend database credentials are invalid or out of date.",
          },
        ),
      ).toEqual({
        status: AUTH_STATUSES.UNAVAILABLE,
        message: "The backend database credentials are invalid or out of date.",
      });
    });
  });
}
